import { useCallback, useEffect, useMemo, useState } from 'react';

const STUDENT_SUITE_STORAGE_CHANGED_EVENT = 'STUDENT_SUITE_STORAGE_CHANGED_EVENT';
const getStorageChangedEventType = (key) => `${STUDENT_SUITE_STORAGE_CHANGED_EVENT}_${key}`;

const STUDENT_SUITE_STORAGE_REMOVED_EVENT = 'STUDENT_SUITE_STORAGE_REMOVED_EVENT';
const getStorageRemovedEventType = (key) => `${STUDENT_SUITE_STORAGE_REMOVED_EVENT}_${key}`;

/**
 * 'storage' event would only be dispatched when another tab changed the storage
 * so we need to dispatch a customized event to inform current tab that the storage has changed
 *
 * @param {string} key
 * @param {any} initialValue
 */
export function useLocalStorage(key, initialValue) {
  if (!key) {
    throw new Error('key must be provided');
  }

  const defaultValue = useMemo(() => {
    const localStorageValue = window.localStorage.getItem(key);

    if (localStorageValue !== null) {
      return localStorageValue;
    } else if (initialValue && localStorageValue !== initialValue) {
      localStorage.setItem(key, initialValue);
      return initialValue;
    }
  }, [initialValue, key]);

  const [value, setValue] = useState(defaultValue);

  const setItem = useCallback(
    (value) => {
      try {
        localStorage.setItem(key, value);
        setValue(value);
        window.dispatchEvent(new Event(getStorageChangedEventType(key)));
      } catch {
        // If user is in private mode or has storage restriction
        // localStorage can throw.
      }
    },
    [key]
  );

  const removeItem = useCallback(() => {
    try {
      localStorage.removeItem(key);
      setValue(undefined);
      window.dispatchEvent(new Event(getStorageRemovedEventType(key)));
    } catch {
      // If user is in private mode or has storage restriction
      // localStorage can throw.
    }
  }, [key]);

  useEffect(() => {
    const listener = () => {
      const valueFromStorage = window.localStorage.getItem(key);
      if (valueFromStorage !== value) setValue(valueFromStorage);
    };

    window.addEventListener(getStorageChangedEventType(key), listener);

    return () => {
      window.removeEventListener(getStorageChangedEventType(key), listener);
    };
  }, [key, value]);

  useEffect(() => {
    const listener = () => {
      const valueFromStorage = window.localStorage.getItem(key);
      if (valueFromStorage) setValue(undefined);
    };

    window.addEventListener(getStorageRemovedEventType(key), listener);

    return () => {
      window.removeEventListener(getStorageRemovedEventType(key), listener);
    };
  }, [key]);

  return [value, setItem, removeItem];
}
